import { HTMLAttributeAnchorTarget, useCallback, useMemo } from "react";

import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import Image from "common/blocks/Image";
import useModalContext from "common/providers/modal/useModalContext";
import { storeDetailsModal } from "features/vehicleDetails/modals";
import useClosestStoresContext from "features/vehicleDetails/providers/closestStores/useClosestStoresContext";
import { ONLINE_STORE_SITE_ID } from "lib/constants";

import SectionLabel from "../SectionLabel";
import styles from "./StoreLocation.module.scss";

const cx = classNames.bind(styles);

export interface StoreLocationProps {
    locationId: number;
    requestACallbackButton: string;
    requestACallbackLink: string;
    requestACallbackTarget: HTMLAttributeAnchorTarget;
    getDirectionsButton: string;
    seeDetailsButton: string;
}

const StoreLocation: React.FC<StoreLocationProps> = (props) => {
    const {
        locationId,
        requestACallbackButton,
        requestACallbackLink,
        requestACallbackTarget,
        getDirectionsButton,
        seeDetailsButton,
    } = props;
    const { allStoresDetailsData } = useClosestStoresContext();

    const { useSidebar } = useModalContext();
    const { openSidebar } = useSidebar();

    const storeData = useMemo(
        () => allStoresDetailsData.find(({ siteId }) => siteId === locationId)!,
        [allStoresDetailsData, locationId]
    );

    const image = storeData?.images[0]!;

    const onSeeDetailsClick = useCallback(() => {
        const onlineStoreData = allStoresDetailsData.find(
            (store: any) => store.siteId === ONLINE_STORE_SITE_ID
        );
        openSidebar(storeDetailsModal(`Motorpoint ${storeData?.siteName}`), {
            storeData,
            onlineStoreData,
        });
    }, [allStoresDetailsData, openSidebar, storeData]);

    return (
        <div className={cx("stack")}>
            <SectionLabel>Location</SectionLabel>
            <div className={cx("store")}>
                {image?.src && (
                    <div className={cx("hero")}>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            id={image?.codename ?? undefined}
                            loading="lazy"
                            className={cx("image")}
                            alt={image?.alt ?? ""}
                            src={image.src}
                            width={405}
                            height={168}
                        />
                    </div>
                )}
                <div className={cx("content")}>
                    <div className={cx("main")}>
                        <span className={cx("heading")}>Motorpoint {storeData?.siteName}</span>
                        <div className={cx("body")}>
                            <span>{storeData.address.line1}</span>
                            <span>{storeData.address.locality}</span>
                            <span>{storeData.address.postcode}</span>
                        </div>
                    </div>
                    <div className={cx("mainActions")}>
                        <Button
                            theme="primary"
                            className={cx("primaryButton")}
                            href={requestACallbackLink}
                            target={requestACallbackTarget}
                            rel={
                                requestACallbackTarget === "_blank"
                                    ? "noreferrer noopener nofollow"
                                    : "nofollow"
                            }
                            ariaLabel={requestACallbackButton}
                        >
                            {requestACallbackButton}
                        </Button>
                        <Button
                            theme="selection"
                            className={cx("phoneButton")}
                            href={`tel:${storeData.phone}`}
                            ariaLabel={`Call ${storeData.phone}`}
                        >
                            {storeData.phone}
                        </Button>
                    </div>
                    <div className={cx("secondaryActions")}>
                        <Button
                            theme="textLink"
                            target="_blank"
                            href={storeData.googleMaps.directionsUrl}
                            ariaLabel={getDirectionsButton}
                            rel="nofollow"
                        >
                            {getDirectionsButton}
                        </Button>
                        <Button
                            theme="textLink"
                            onClick={onSeeDetailsClick}
                            ariaLabel={seeDetailsButton}
                        >
                            {seeDetailsButton}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StoreLocation;
